import { Line } from 'rc-progress';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { COLORS } from '../../theme';
import { Container as SellerProfileContainer } from './SellerProfile';
import { Circle } from 'rc-progress';
import { Card } from '@tremor/react';
import { CashIcon } from '@heroicons/react/solid';
import Link from 'next/link';

export const Container = styled(SellerProfileContainer)`
  && {
    max-width: 90vw;
  }
  padding: 1rem;
  @media only screen and (max-width: 1100px) {
    border: 1px solid #e5e8f0;
    box-shadow: 0 0;
  }
`;

export const Text = styled.p`
  text-align: left;
  font: normal normal 600 16px/25px Poppins;
  letter-spacing: 0px;
  color: #111723;
  margin-left: 1rem;
  opacity: 1;
`;

type ColorProps = {
  color: string;
};

const LargeText = styled.p`
  text-align: left;
  font: normal normal 500 0.95rem/16px Poppins;
  letter-spacing: 0px;
  color: ${(props: ColorProps) => props.color};
  vertical-align: bottom;
  display: inline;
`;

const SmallText = styled.p`
  text-align: left;
  font: normal normal 500 0.625rem/16px Poppins;
  letter-spacing: 0px;
  color: ${(props: ColorProps) => props.color};
  vertical-align: bottom;
  display: inline;
  margin-left: 0.25rem;
`;

const ProgressRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ProgressTextContainer = styled.div``;

export const RewardContainer = styled.div`
  background: #f6f9fc 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 16px ${COLORS.ACCENT}29;
  border: 1px solid ${COLORS.ACCENT};
  opacity: 1;
  height: 2.25rem;
  width: 40%;
  min-width: 200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RewardText = styled.p`
  text-align: left;
  font: normal normal normal 0.5rem/12px Poppins;
  letter-spacing: 0.25px;
  color: ${COLORS.ACCENT};
  opacity: 1;
`;

const CircleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 0px 57px #919db529;
  opacity: 1;
  border-radius: 100%;
  position: relative;
`;

const CircleText = styled.p`
  text-align: left;
  font: normal normal 600 20px/30px Poppins;
  letter-spacing: 0px;
  color: ${COLORS.LIGHT_ACCENT};
  opacity: 1;
  position: absolute;
  top: 50%;
  margin: 0;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const StyledCircle = styled(Circle)``;

type Props = {
  sales: number;
  sellerLevel: {
    id: string;
    name: string;
    feePercentage: number;
    salesRequired: number;
  };
  nextSellerLevel: {
    id: string;
    name: string;
    feePercentage: number;
    salesRequired: number;
  };
};

export const SalesToLevel = (props: Props) => {
  const [percent, setPercent] = useState(0);
  const finalPercent = props.nextSellerLevel
    ? (props.sales / props.nextSellerLevel.salesRequired) * 100
    : 100;

  useEffect(() => {
    setTimeout(() => {
      if (percent < finalPercent) {
        setPercent(percent + 1);
      }
    }, 5);
  }, [percent]);

  return (
    <Card className="h-full">
      <div className="flex flex-row mb-4">
        <Text className="w-fit my-auto">Sales until next level</Text>

        <Link
          href="/dashboard/transactions/sales"
          className="ml-auto font-bold text-md text-tremor-brand"
        >
          <p className="flex">
            View Sales
            <CashIcon className="w-6 h-6 p-1 hidden md:block" />
          </p>
        </Link>
      </div>
      <div className="flex sm:flex-row flex-col w-full items-center">
        <div className="flex flex-row gap-2 mt-2 md:mt-0 mx-0 md:mx-4">
          <div>
            <CircleContainer className="h-32 w-32">
              <StyledCircle
                className="h-28 w-28 overflow-visible m-5 p-2"
                percent={
                  props.nextSellerLevel
                    ? (props.sales / props.nextSellerLevel.salesRequired) * 100
                    : 100
                }
                strokeWidth={10}
                trailWidth={10}
                trailColor="#F2F5F9"
                strokeColor={COLORS.LIGHT_ACCENT}
                strokeLinecap="square"
              />
              <CircleText className="text-sm text-center">
                Level {props.sellerLevel.name} {props.sellerLevel.feePercentage}
                %
              </CircleText>
            </CircleContainer>
          </div>
          {/* <InfoContainer className="my-auto ml-2">
            <TextWithLine title={String(props.sales)} subtitle="Sales" />
            <TextWithLine
              title={`${props.sellerLevel.feePercentage}%`}
              subtitle="Fee"
            />
          </InfoContainer> */}
        </div>
        <div className="items-center mx-auto">
          <div className="flex flex-row justify-between">
            <div>
              <LargeText color={COLORS.ACCENT}>{props.sales}</LargeText>
              <SmallText color={COLORS.ACCENT}>sales completed</SmallText>
            </div>
            <div>
              {props.nextSellerLevel && (
                <>
                  <LargeText color="#717884">
                    {props.nextSellerLevel
                      ? props.nextSellerLevel.salesRequired - props.sales
                      : 0}
                  </LargeText>
                  <SmallText color="#717884">more needed</SmallText>
                </>
              )}
              {!props.nextSellerLevel && (
                <>
                  <SmallText color="#717884">Max level reached</SmallText>
                </>
              )}
            </div>
          </div>
          <div className="flex mb-4">
            <Line
              style={{
                marginTop: '.5rem',
                borderRadius: 20,
                height: '1rem',
                width: '100%',
              }}
              percent={percent}
              strokeWidth={3}
              trailWidth={15}
              trailColor="#E2E3E3"
              strokeLinecap="round"
              strokeColor={COLORS.LIGHT_ACCENT}
            />
          </div>

          <RewardContainer>
            <RewardText>
              {props.nextSellerLevel
                ? `Level ${props.nextSellerLevel.name} Reward: ${props.nextSellerLevel.feePercentage}% Transaction Fee`
                : 'You have reached max level'}
            </RewardText>
          </RewardContainer>
        </div>
      </div>
    </Card>
  );
};
